import { WorkingState, CapabilityEntry, CapabilityDomain, CAPABILITY_DOMAINS } from '../types/persona_engine';

/**
 * Converts any capability shape (legacy flat int, or already-rich CapabilityEntry)
 * to Record<string, CapabilityEntry>. Safe to call on null/undefined.
 */
export function migrateCapabilities(
  caps: Record<string, any> | null | undefined
): Record<string, CapabilityEntry> {
  if (!caps) return {};
  const entries = Object.entries(caps)
    .map(([key, val]): [string, CapabilityEntry] | null => {
      if (typeof val === 'number') {
        // Legacy flat integer — wrap as minimal CapabilityEntry
        return [key, {
          key,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          score: Math.max(0, Math.min(10, val)),
          report: '',
          evidence: [],
          ceiling: '',
          floor: '',
          domain: 'domain_specific' as CapabilityDomain,
        }];
      }
      if (typeof val === 'object' && val !== null && 'score' in val) {
        // Already a CapabilityEntry — pass through, clamp score
        return [key, { ...val, score: Math.max(0, Math.min(10, val.score)) }];
      }
      // Unknown shape — skip
      return null;
    })
    .filter((e): e is [string, CapabilityEntry] => e !== null);
  return Object.fromEntries(entries);
}

/**
 * Safe score extractor — handles both legacy int and CapabilityEntry shapes.
 */
export function getCapabilityScore(val: CapabilityEntry | number | any): number {
  if (typeof val === 'number') return Math.max(0, Math.min(10, val));
  if (typeof val === 'object' && val !== null && 'score' in val) {
    return Math.max(0, Math.min(10, val.score));
  }
  return 0;
}

// ── Honest-signal helpers (SP-C) ──────────────────────────────────────────
// PATH DISCIPLINE: the canonical rich-capability + probe data lives at the
// NESTED engineering_profile path below. Top-level `persona.capabilities` is
// contaminated (sparse ints AND ~40 live report-bearing objects) and must
// NEVER feed a gauge — only `_currentState(persona).capabilities` does.
function _currentState(persona: any): any {
  return persona?.data?.data?.engineering_profile?.current_state
      ?? persona?.data?.engineering_profile?.current_state
      ?? {};
}

/** Rich (forge-real) capability entries only — migrated-legacy ints carry an
 *  empty report, so report-non-empty is the honest "has real score" gate. */
export function realCapabilityEntries(persona: any): CapabilityEntry[] {
  const migrated = migrateCapabilities(_currentState(persona).capabilities);
  return Object.values(migrated).filter((e) => !!e.report && e.report.trim() !== '');
}

export function hasRealCapabilityScores(persona: any): boolean {
  return realCapabilityEntries(persona).length > 0;
}

/** Count nested caps carrying a numeric score (incl. scored-but-report-less
 *  hybrids). ≥ realCapabilityEntries; the gap is the radar's silent truncation
 *  (F6) — DossierDrawer names it rather than dropping spokes unannounced. */
export function scoredCapabilityCount(persona: any): number {
  const caps = _currentState(persona).capabilities;
  if (!caps || typeof caps !== 'object') return 0;
  return Object.values(caps).filter(
    (v: any) => typeof v === 'number'
      || (typeof v === 'object' && v !== null && typeof v.score === 'number'),
  ).length;
}

/** Universal honest gauge — works for every persona with field-tested probes.
 *  F1: reads `last_passed` (the real field; 0/87 live probes carry `passed`),
 *  falls back to the last run_history entry, EXCLUDES untested/pending probes,
 *  and returns null when there's no signal — never a confident fake 0%. */
export function probePassRate(persona: any): { rate: number; passed: number; total: number } | null {
  const probes = _currentState(persona).probes;
  if (!Array.isArray(probes) || probes.length === 0) return null;
  const stateOf = (p: any): boolean | null =>
    typeof p?.last_passed === 'boolean'
      ? p.last_passed
      : (Array.isArray(p?.run_history) && p.run_history.length
          ? !!p.run_history[p.run_history.length - 1]?.passed
          : null);
  const tested = probes.map(stateOf).filter((v): v is boolean => v !== null);
  if (tested.length === 0) return null;
  const passed = tested.filter(Boolean).length;
  return { rate: Math.round((passed / tested.length) * 100), passed, total: tested.length };
}

/** Party-level domain coverage from REAL capability domains (no fake synergy).
 *  Personas without rich entries contribute via field-readiness (probe gauge),
 *  not here — domain coverage stays honest to the 10-domain taxonomy. */
export function partyDomainCoverage(personas: any[]): {
  covered: CapabilityDomain[]; thin: CapabilityDomain[];
} {
  const seen = new Set<CapabilityDomain>();
  for (const p of personas) for (const e of realCapabilityEntries(p)) seen.add(e.domain);
  return {
    covered: CAPABILITY_DOMAINS.filter((d) => seen.has(d)),
    thin: CAPABILITY_DOMAINS.filter((d) => !seen.has(d)),
  };
}


/**
 * Assembles a controller prompt from the identity core components.
 * Creates a deterministic string representation for LLM input.
 * 
 * @param state The current working state
 * @returns A formatted controller prompt string
 */
export function assembleControllerPrompt(state: WorkingState): string {
  // Create a deterministic representation of the identity core
  const controllerData = {
    character_bible: state.character_bible,
    tethics: state.tethics,
    patterns: state.patterns,
    antipatterns: state.antipatterns,
    ...(state.tags && { tags: state.tags }),
    ...(state.capabilities && { capabilities: state.capabilities })
  };
  
  // Ensure stable key ordering for consistent hashing
  return JSON.stringify(controllerData, Object.keys(controllerData).sort());
}

/**
 * Computes a hash of the source data (character bible, tethics, patterns, antipatterns)
 * Used for detecting changes and determining when recompilation is needed
 * 
 * @param state The current working state
 * @returns A hash string representing the current source state
 */
export async function computeSourceHash(state: WorkingState): Promise<string> {
  const sourceData = {
    bible: state.character_bible?.trim() || '',
    tethics: (state.tethics || []).map(t => t.trim()).sort(),
    patterns: (state.patterns || []).map(p => p.trim()).sort(),
    antipatterns: (state.antipatterns || []).map(a => a.trim()).sort(),
    capabilities: state.capabilities || {}
  };
  return sha256(JSON.stringify(sourceData));
}

/**
 * Computes a SHA-256 hash of the provided text.
 * Used for caching and change detection.
 * 
 * @param text The text to hash
 * @returns A hex string representation of the hash
 */
export async function sha256(text: string): Promise<string> {
  try {
    console.log('sha256 called with text length:', text?.length);
    console.log('window.crypto available:', !!window.crypto);
    console.log('window.crypto.subtle available:', !!(window.crypto && window.crypto.subtle));
    
    // Try to use the Web Crypto API if available
    if (window.crypto && window.crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(text);
      console.log('Data encoded, calling crypto.subtle.digest');
      const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
      console.log('Digest completed, creating hash array');
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const result = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      console.log('Hash generated successfully:', result.substring(0, 10) + '...');
      return result;
    }
    
    // If Web Crypto API is not available, use a simple string hash function
    console.error('Web Crypto API not available, using fallback hash function');
    
    // Simple string hash function
    let hash = 0;
    if (text.length === 0) return '0'.repeat(64);
    
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    // Convert to hex string and ensure it's 64 characters long (like SHA-256)
    const hashHex = Math.abs(hash).toString(16).padStart(8, '0');
    const repeatedHash = hashHex.repeat(8).substring(0, 64);
    
    console.log('Using fallback hash algorithm:', repeatedHash.substring(0, 10) + '...');
    return repeatedHash;
  } catch (error) {
    console.error('Error in sha256 function:', error);
    
    // Fallback hash in case of error
    let fallbackHash = 'error-hash-' + Date.now();
    fallbackHash = fallbackHash.padEnd(64, '0');
    return fallbackHash;
  }
}

/**
 * Computes all hashes for a working state.
 * Updates controller_prompt_hash, ui_schema_hash, and system_prompt_hash.
 * 
 * @param state The working state to compute hashes for
 * @returns An updated working state with computed hashes
 */
export async function computeStateHashes(state: WorkingState): Promise<WorkingState> {
  const updatedState = { ...state };
  
  // Compute controller_prompt_hash
  if (state.controller_prompt) {
    updatedState.controller_prompt_hash = await sha256(state.controller_prompt);
  }
  
  // Compute ui_schema_hash
  if (state.ui_schema && state.ui_schema.length > 0) {
    updatedState.ui_schema_hash = await sha256(JSON.stringify(state.ui_schema));
  }
  
  // Compute system_prompt_hash
  if (state.system_prompt) {
    updatedState.system_prompt_hash = await sha256(state.system_prompt);
  }
  
  return updatedState;
}
